import React from 'react';
import { Shield, Lock, Search, Loader2 } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { useParams } from 'react-router-dom';
import { useUtilityInfo, useConsumerDetail, useGenerateOtp } from '../../../../../auth/hooks';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';

interface Props {
  onNext?: () => void;
  data?: any;
  onDataChange?: (data: any) => void;
}

export const VerifyIdentity: React.FC<Props> = ({ onNext, data, onDataChange }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const accountNumber = data?.accountNumber || '';
  
  const setAccountNumber = (val: string) => {
    onDataChange?.({ ...data, accountNumber: val });
  };

  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  
  const tenantAlias = tenant || localStorage.getItem('tenant') || '';
  const utilityAlias = utilityName || localStorage.getItem('utilityName') || '';

  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenantAlias, utility_alias: utilityAlias },
    { enabled: !!tenantAlias && !!utilityAlias }
  );

  const { refetch, isFetching, data: consumerDetailData } = useConsumerDetail(
    { 
      remote_utility_id: utilityInfo?.result?.id, 
      consumer_no: accountNumber 
    },
    { enabled: false }
  );

  const generateOtpMutation = useGenerateOtp();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountNumber) return;
    
    const result = await refetch();
    if (result.isSuccess && result.data && result.data.result) {
      const email = result.data.result?.email;
      if (!email) {
        toast({
          title: "Error",
          description: result.data?.message || t("No email found in consumer details"),
          variant: "destructive",
        });
        return;
      }
      
      onDataChange?.({
        ...data,
        accountNumber,
        accountData: result.data.result
      });

      const payload = {
        email,
        remote_utility_id: utilityInfo?.result?.id
      };

      generateOtpMutation.mutate(payload, {
        onSuccess: (res) => {
          sessionStorage.setItem('guest_portal_email', email);
          const secretKey = 
            res?.otp?.result?.secretKey || 
            res?.result?.otp?.result?.secretKey || 
            res?.data?.otp?.result?.secretKey || 
            res?.secretKey;
            
          if (secretKey) {
            sessionStorage.setItem('guest_portal_secret_key', secretKey);
          }
          onNext?.();
        },
        onError: (err: any) => {
          console.error("Failed to generate OTP:", err);
          const errorMessage = err?.response?.data?.message || err?.message || t("Failed to generate OTP.");
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        }
      });
    } else {
      console.error("Verification failed", result.error);
      const errorMessage = 
        (result.error as any)?.response?.data?.message || 
        (result.error as any)?.message || 
        result.data?.message || 
        t("Error while fetching the Consumer detail");

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column: Form */}
      <div className="flex-1">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{t("Verify Your Account")}</h2>
              <p className="text-gray-500 text-sm">{t("Enter your account details to find your bills")}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                {t("Account Number")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400 font-medium">#</span>
                <input 
                  type="text" 
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder={t("e.g. WTR-2024-007777")}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all placeholder:font-light"
                  required
                />
              </div>
            </div>

            <Button 
             variant="default"
              disabled={!accountNumber || isFetching || generateOtpMutation.isPending}
              className="w-full h-12 text-base"
            >
              {isFetching || generateOtpMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 w-5 h-5" />} {t("Find My Bills")}
            </Button>
          </form>
        </div>
      </div>

      {/* Right Column: Info */}
      <div className="w-full lg:w-[380px] space-y-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6 ">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg">{t("Secure Guest Payment")}</h3>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-5 h-5 rounded-full border border-green-500 flex items-center justify-center text-green-500 shrink-0">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              {t("Verify your account with OTP")}
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-5 h-5 flex items-center justify-center text-blue-500 shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
              </div>
              {t("View and select unpaid bills")}
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-5 h-5 flex items-center justify-center text-purple-500 shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              {t("Choose your payment method")}
            </li>
            <li className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-5 h-5 flex items-center justify-center text-teal-500 shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              {t("Complete secure payment")}
            </li>
          </ul>

          <div className="flex items-center gap-2 text-xs text-gray-400 border-t border-gray-100 pt-4">
            <Lock className="w-3.5 h-3.5 text-green-500" /> {t("256-bit TLS · PCI DSS Compliant")}
          </div>
        </div>

        <div className="bg-[#f4f7f9] rounded-2xl p-5 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-green-600" />
            <h4 className="font-bold text-gray-700 text-sm">{t("Payment Security")}</h4>
          </div>
          <ul className="text-xs text-gray-500 space-y-2 font-medium">
            <li>• {t("256-bit TLS encryption")}</li>
            <li>• {t("PCI DSS Level 1 compliant")}</li>
            <li>• {t("Powered by Stripe")}</li>
            <li>• {t("No card data stored on our servers")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
